import type { DiagnosticScaleValue } from '@/domains/assessment/types';
import { SCALE_LABELS } from '@/domains/assessment/constants';

export interface RadioScaleProps {
  name: string;
  value: DiagnosticScaleValue | null;
  onChange: (value: DiagnosticScaleValue) => void;
  options?: Record<DiagnosticScaleValue, string>;
  disabled?: boolean;
  className?: string;
}

export function RadioScale({
  name,
  value,
  onChange,
  options,
  disabled = false,
  className = '',
}: RadioScaleProps) {
  const scaleOptions: DiagnosticScaleValue[] = [0, 1, 2, 3];
  const labels = options || SCALE_LABELS;

  return (
    <fieldset
      className={`flex flex-col gap-3 ${className}`}
      disabled={disabled}
      aria-label="Escala de avaliação da pergunta"
    >
      <legend className="sr-only">Selecione uma das 4 opções de resposta</legend>
      {scaleOptions.map((optionValue) => {
        const isSelected = value === optionValue;
        const inputId = `${name}-option-${optionValue}`;

        return (
          <label
            key={optionValue}
            htmlFor={inputId}
            className={`group relative flex min-h-[56px] cursor-pointer items-center rounded-xl border p-4 transition-all duration-150 select-none ${
              isSelected
                ? 'border-brand-orange bg-brand-orange/10 shadow-md ring-1 ring-brand-orange'
                : 'border-white/10 bg-navy-light/40 hover:border-white/30 hover:bg-navy-light/60'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <input
              type="radio"
              id={inputId}
              name={name}
              value={optionValue}
              checked={isSelected}
              disabled={disabled}
              onChange={() => onChange(optionValue)}
              className="sr-only"
            />

            {/* Indicador visual de rádio com alvo acessível */}
            <div
              className={`mr-4 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all ${
                isSelected
                  ? 'border-brand-orange bg-brand-orange text-white'
                  : 'border-white/30 bg-white/5 group-hover:border-white/50'
              } group-focus-within:ring-2 group-focus-within:ring-brand-orange group-focus-within:ring-offset-2 group-focus-within:ring-offset-navy`}
              aria-hidden="true"
            >
              {isSelected && <div className="h-2.5 w-2.5 rounded-full bg-white" />}
            </div>

            {/* Número e Rótulo da escala */}
            <div className="flex flex-1 items-center justify-between gap-3">
              <span
                className={`text-sm font-medium leading-relaxed transition-colors sm:text-base ${
                  isSelected ? 'font-semibold text-white' : 'text-brand-offWhite/90'
                }`}
              >
                {labels[optionValue]}
              </span>
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                  isSelected
                    ? 'bg-brand-orange/30 text-brand-orange'
                    : 'bg-white/5 text-brand-offWhite/40'
                }`}
              >
                {optionValue}
              </span>
            </div>
          </label>
        );
      })}
    </fieldset>
  );
}
